import React from "react";
import { X, FileText, ShieldCheck } from "lucide-react";

export const TermsModal = ({ open, onClose, brand = "this platform" }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur grid place-items-center z-[60] p-4" onClick={onClose}>
      <div data-testid="terms-modal" className="bg-white text-slate-900 rounded-3xl max-w-lg w-full max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-tenant-primary" />
            <h2 className="font-extrabold text-lg">Terms & Conditions</h2>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[70vh] text-[12px] leading-relaxed text-slate-700 space-y-3">
          <p>Effective Date: {new Date().toLocaleDateString()}.</p>
          <Section title="1. Acceptance of Terms">
            By logging into <b>{brand}</b>, you agree to these Terms & Conditions. If you do not agree, please do not use the service.
          </Section>
          <Section title="2. Eligibility">
            You must be 18+ and resident in India to open a Saving Wallet, hold a policy, or refer customers. You confirm that the personal information you provide is accurate.
          </Section>
          <Section title="3. Wallet & Daily Collections">
            Your Saving Wallet may be credited by you, your assigned agent or the platform admin. Daily collection amounts are advisory and at the discretion of the tenant/agent. Bonus Wallet credits are subject to admin verification.
          </Section>
          <Section title="4. Payments & Refunds">
            All payments are processed through PCI-DSS compliant gateways (Razorpay). Refunds for ineligible top-ups will be processed within 7 working days subject to tenant approval. Subscription fees paid by tenants are non-refundable after activation.
          </Section>
          <Section title="5. Referrals & Lucky Draw">
            Referral rewards are credited only after the referee makes their first contribution. Lucky Draw participation requires an active policy of at least 6 months and is subject to admin's published rules. Decisions are final.
          </Section>
          <Section title="6. KYC & Anti-Fraud">
            You authorise the platform to verify your KYC (Aadhaar, PAN, address) with applicable bureaus. Fraudulent activity may lead to immediate suspension and forfeiture of bonuses.
          </Section>
          <Section title="7. Communications">
            By signing up you consent to receive transactional SMS/WhatsApp/Email messages for OTPs, payment confirmations, due reminders and lucky-draw announcements.
          </Section>
          <Section title="8. Limitation of Liability">
            The platform is not a bank or insurer. Tenants (Financial Advisors) are responsible for their policy and premium collection commitments. Maximum liability of the platform is capped at the fees paid by the user for the affected period.
          </Section>
          <Section title="9. Suspension & Termination">
            We may suspend or terminate accounts for breach, non-payment of subscription, or upon legal request. You may close your account anytime by contacting support; pending wallet balances will be settled per the refund clause.
          </Section>
          <Section title="10. Governing Law">
            These terms are governed by Indian law and disputes are subject to the exclusive jurisdiction of the courts at the tenant's registered office.
          </Section>
        </div>
      </div>
    </div>
  );
};

export const PrivacyModal = ({ open, onClose, brand = "this platform" }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur grid place-items-center z-[60] p-4" onClick={onClose}>
      <div data-testid="privacy-modal" className="bg-white text-slate-900 rounded-3xl max-w-lg w-full max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-tenant-primary" />
            <h2 className="font-extrabold text-lg">Privacy Policy</h2>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[70vh] text-[12px] leading-relaxed text-slate-700 space-y-3">
          <Section title="1. What we collect">
            Name, phone, Aadhaar/PAN (when KYC required), nominee details, wallet balances, transactions, device IP, and OTP delivery logs.
          </Section>
          <Section title="2. How we use it">
            To authenticate you, process wallet credits/debits, send transactional messages, calculate referrals/bonuses, and meet RBI/DLT regulatory requirements.
          </Section>
          <Section title="3. Storage & security">
            Data is stored in encrypted MongoDB clusters; secrets are hashed; payment credentials are never stored on our servers (PCI-DSS scope offloaded to Razorpay).
          </Section>
          <Section title="4. Sharing">
            With your tenant (Financial Advisor), with payment gateways, and only when legally required. We never sell your data.
          </Section>
          <Section title="5. Your rights">
            Access, correct, export or delete your data by writing to your tenant's support email. We respond within 30 days.
          </Section>
          <Section title="6. Cookies & tracking">
            Only essential cookies for session and PWA install state. No third-party advertising trackers.
          </Section>
          <Section title="7. Children">
            Service is intended for users 18+. Accounts of minors will be deleted on verification.
          </Section>
          <Section title="8. Changes">
            We will notify you in-app of material changes 7 days prior to taking effect.
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="font-extrabold text-slate-900 text-[13px]">{title}</div>
    <div className="mt-0.5">{children}</div>
  </div>
);
